import React from 'react';
import _ from 'lodash';
import { FieldSet, TextCollectionFieldType } from '@scale/shared/types/params/textcollection';
import { AutoCompleteQuery } from './autoComplete';
import {
  TextCollectionAnnotation,
  FieldSetAnnotation,
  TextFieldAnnotation,
} from '@scale/shared/types/response/textcollection';
import { getFirstTextField, getFieldSetTitle, getFieldSetCounts } from './fieldSetUtils';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { TextField, Button, Link, makeStyles } from '@material-ui/core';
// @ts-ignore
import pluralize from 'pluralize';

interface FieldSetFormProps {
  field: FieldSet;
  annotation: FieldSetAnnotation;
  addResponse: (fieldSetResponse: Record<string, TextCollectionAnnotation>) => void;
  autoComplete: (query: AutoCompleteQuery) => Record<string, TextCollectionAnnotation>[];
}

export default function FieldSetForm(props: FieldSetFormProps) {
  const styles = useFieldSetFormStyles();
  const [name, setName] = React.useState<string>('');
  const [results, setResults] = React.useState<Record<string, TextCollectionAnnotation>[]>([]);
  const textFieldRef = React.useRef<HTMLInputElement>(null);

  const firstTextField = getFirstTextField(props.field);

  const onChangeName = function (event: React.ChangeEvent<HTMLInputElement>): void {
    setName(event.target.value);
    if (firstTextField) {
      const query: AutoCompleteQuery = {
        fieldSet: props.field.field_id,
        field: firstTextField.field_id,
        search: event.target.value,
      };
      setResults(props.autoComplete(query));
    }
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const firstTextField = getFirstTextField(props.field);
    if (firstTextField) {
      const textAnnotation: TextFieldAnnotation = {
        type: TextCollectionFieldType.Text,
        field_id: firstTextField.field_id,
        response: [name],
      };
      const fieldSetResponse: Record<string, TextCollectionAnnotation> = {
        [firstTextField.field_id]: textAnnotation,
      };
      props.addResponse(fieldSetResponse);
      setName('');
    }
  };

  const nonDuplicateResults = results.filter(result => {
    return !props.annotation.response.includes(result);
  });

  return (
    <form className={styles.addNewForm} onSubmit={onSubmit}>
      <div className={styles.addNewCard}>
        <TextField
          inputRef={textFieldRef}
          autoFocus
          fullWidth
          value={name}
          onChange={onChangeName}
          variant="outlined"
          InputProps={{
            classes: { root: styles.fieldSetTitleRoot, input: styles.fieldSetTitleInput },
          }}
        />
        <Button
          classes={{ root: styles.addNewSubmit, textPrimary: styles.buttonTextPrimary }}
          color="primary"
          variant="text"
          type="submit"
          disabled={name === ''}
        >
          Add
        </Button>
      </div>
      <div
        className={styles.autoCompleteCard}
        style={{ display: nonDuplicateResults.length ? 'block' : 'none' }}
      >
        <div className={styles.autoCompleteHeading}>Use an existing {props.field.title}</div>
        {nonDuplicateResults.map((fieldSetResponse, index) => {
          const title = getFieldSetTitle(props.field, fieldSetResponse);
          const counts = getFieldSetCounts(props.field.fields, fieldSetResponse);
          const messages = counts.map(data => {
            const title = pluralize(data.title.toLowerCase(), data.count, false);
            return `${data.count} ${title}`;
          });
          const onClick = () => {
            props.addResponse(_.cloneDeep(fieldSetResponse));
          };
          return (
            <div key={`${title} ${index}`}>
              <Link
                classes={{ root: styles.autoCompleteLink }}
                component="div"
                onClick={onClick}
                underline="none"
              >
                <span>{title}</span>
                <span className={styles.autoCompleteHint}>{messages.join(' ')}</span>
              </Link>
            </div>
          );
        })}
      </div>
    </form>
  );
}

const useFieldSetFormStyles = makeStyles({
  addNewForm: {
    paddingLeft: '30px',
    paddingRight: '30px',
  },
  addNewCard: {
    background: '#FFFFFF',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.12)',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '8px',
  },
  addNewSubmit: {
    marginLeft: '8px',
  },
  autoCompleteCard: {
    background: '#FFFFFF',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.12)',
    borderRadius: '8px',
    margin: '8px',
    padding: '2px 0',
    maxWidth: '300px',
  },
  autoCompleteHeading: {
    color: '#808080',
    fontSize: '11px',
    lineHeight: '16px',
    fontWeight: 500,
    padding: '2px 16px',
    letterSpacing: '0.1px',
  },
  autoCompleteLink: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 16px',
    cursor: 'pointer',
    '&:hover': {
      background: '#F2F2F2',
    },
    '&:focus': {
      background: '#F2F2F2',
    },
  },
  autoCompleteHint: {
    color: '#808080',
  },
  buttonTextPrimary: {
    color: '#2B99D8',
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: '20px',
    letterSpacing: '0.1px',
  },
  addNewButton: {
    marginBottom: '8px',
  },
  fieldSetTitleRoot: {
    border: '1px solid #CCCCCC',
    borderRadius: '4px',
  },
  fieldSetTitleInput: {
    paddingTop: '16px',
    paddingBottom: '16px',
    fontSize: '14px',
    lineHeight: '16px',
    letterSpacing: '0.1px',
  },
});
